import React, { useCallback, useEffect, useRef, useState } from 'react';
import { vaultAPI } from '../services/api';
import { parseApiError } from '../utils/errors';

// How long a revealed password stays on screen before it is wiped from state.
const VISIBLE_SECONDS = 30;

/**
 * The only place a stored password is ever shown.
 *
 * The flow is deliberately two-step: the caller re-enters their own login
 * password, the server checks it and writes an access-log row, and only then
 * is the secret returned. It is held in state for VISIBLE_SECONDS and then
 * cleared, so a walked-away-from screen does not keep it on display.
 */
export default function VaultRevealModal({ entry, onClose }) {
  const [password, setPassword] = useState('');
  const [secret, setSecret] = useState('');
  const [remaining, setRemaining] = useState(VISIBLE_SECONDS);
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const timerRef = useRef(null);

  const wipe = useCallback(() => {
    setSecret('');
    setPassword('');
    setShow(false);
    setCopied(false);
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
  }, []);

  // Clear the secret if the component goes away for any reason.
  useEffect(() => wipe, [wipe]);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      const { data } = await vaultAPI.reveal(entry.id, password);
      setSecret(data.password);
      setPassword('');           // the confirmation password is no longer needed
      setRemaining(VISIBLE_SECONDS);
      timerRef.current = setInterval(() => {
        setRemaining((r) => {
          if (r <= 1) { wipe(); return 0; }
          return r - 1;
        });
      }, 1000);
    } catch (err) {
      setError(parseApiError(err, 'Could not reveal this password'));
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(secret);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError('Your browser blocked clipboard access — select the text and copy manually.');
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>🔐 {entry.title}</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          {entry.account_identifier && (
            <div className="form-group">
              <label>Username / ID</label>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <code style={{ flex: 1, padding: '8px 10px', background: 'var(--bg)', borderRadius: 6, overflowWrap: 'anywhere' }}>
                  {entry.account_identifier}
                </code>
              </div>
            </div>
          )}

          {!secret ? (
            <form onSubmit={submit}>
              <p className="text-muted" style={{ marginBottom: 14 }}>
                Confirm your own login password to view this. Every reveal is
                recorded in the access log.
              </p>
              {error && <div className="alert alert-danger">{error}</div>}
              <div className="form-group">
                <label>Your login password *</label>
                <input
                  type="password" autoFocus required value={password}
                  autoComplete="current-password"
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Your TaskTool password"
                />
              </div>
              <div className="form-actions">
                <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={busy || !password}>
                  {busy ? 'Checking…' : 'Reveal password'}
                </button>
              </div>
            </form>
          ) : (
            <>
              <div className="form-group">
                <label>Password</label>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <code style={{
                    flex: 1, padding: '10px 12px', background: 'var(--bg)', borderRadius: 6,
                    fontSize: 15, letterSpacing: show ? 0 : 2, overflowWrap: 'anywhere',
                  }}>
                    {show ? secret : '•'.repeat(Math.min(secret.length, 24))}
                  </code>
                  <button type="button" className="btn btn-sm btn-ghost" onClick={() => setShow(!show)}>
                    {show ? 'Hide' : 'Show'}
                  </button>
                  <button type="button" className="btn btn-sm btn-primary" onClick={copy}>
                    {copied ? '✓ Copied' : 'Copy'}
                  </button>
                </div>
              </div>
              {error && <div className="alert alert-danger">{error}</div>}
              <div className="alert alert-warning" style={{ marginTop: 12 }}>
                Hidden again in <strong>{remaining}s</strong>. Do not forward this
                password or save it outside TaskTool.
              </div>
              <div className="form-actions">
                <button type="button" className="btn btn-ghost" onClick={() => { wipe(); onClose(); }}>
                  Done
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
